import { Trade, TradeType, ChainId } from '@uniswap/sdk'
import React, { useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Field } from '../../state/swap/actions'
import { useUserSlippageTolerance } from '../../state/user/hooks'
import { TYPE } from '../../theme'
import { getCurrencySymbol } from '../../utils/getCurrencySymbol'
import { computeSlippageAdjustedAmounts, computeTradePriceBreakdown } from '../../utils/prices'
import { AutoColumn } from '../Column'
import QuestionHelper from '../QuestionHelper'
import { RowBetween, RowFixed } from '../Row'
import FormattedPriceImpact from './FormattedPriceImpact'
import { SectionBreak } from './styleds'
import SwapRoute from './SwapRoute'
import RouteSelectionModal from './RouteSelectionModal'
import { ClickableText } from '../../pages/Pool/styleds'
import { ChevronDown } from 'react-feather'
import { useState } from 'react'

// 根据链ID获取费用百分比
function getFeePercentage(chainId: ChainId): string {
  switch (chainId) {
    case ChainId.BSC:
      return '0.25'
    default:
      return '0.30'
  }
}

function TradeSummary({ trade, allowedSlippage }: { trade: Trade; allowedSlippage: number }) {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const { priceImpactWithoutFee, realizedLPFee } = computeTradePriceBreakdown(trade)
  const isExactIn = trade.tradeType === TradeType.EXACT_INPUT
  const slippageAdjustedAmounts = computeSlippageAdjustedAmounts(trade, allowedSlippage)
  const chainId = trade.route.chainId
  const inputSymbol = getCurrencySymbol(trade.inputAmount.currency, chainId)
  const outputSymbol = getCurrencySymbol(trade.outputAmount.currency, chainId)

  return (
    <>
      <AutoColumn style={{ padding: '0 20px' }}>
        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {isExactIn ? t('minimumReceived') : t('maximumSold')}
            </TYPE.black>
            <QuestionHelper text={t('transactionRevertWarning')} />
          </RowFixed>
          <RowFixed>
            <TYPE.black color={theme.text1} fontSize={14}>
              {isExactIn
                ? `${slippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(4)} ${outputSymbol}`
                : `${slippageAdjustedAmounts[Field.INPUT]?.toSignificant(4)} ${inputSymbol}`}
            </TYPE.black>
          </RowFixed>
        </RowBetween>
        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {t('priceImpact')}
            </TYPE.black>
            <QuestionHelper text={t('priceImpactDescription')} />
          </RowFixed>
          <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
        </RowBetween>

        <RowBetween>
          <RowFixed>
            <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
              {t('liquidityProviderFee')}
            </TYPE.black>
            <QuestionHelper
              text={t('liquidityProviderFeeDescription', { fee: getFeePercentage(trade.route.pairs[0].chainId) })}
            />
          </RowFixed>
          <TYPE.black fontSize={14} color={theme.text1}>
            {realizedLPFee ? `${realizedLPFee.toSignificant(4)} ${inputSymbol}` : '-'}
          </TYPE.black>
        </RowBetween>
      </AutoColumn>
    </>
  )
}

export interface AdvancedSwapDetailsProps {
  trade?: Trade
  trades?: Trade[]
  selectedTrade?: Trade | null
  onTradeSelect?: (trade: Trade) => void
}

export function AdvancedSwapDetails({ trade, trades, selectedTrade, onTradeSelect }: AdvancedSwapDetailsProps) {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false)

  const [allowedSlippage] = useUserSlippageTolerance()

  const showRoute = Boolean(trade)

  return (
    <AutoColumn gap="md">
      {trade && (
        <>
          <TradeSummary trade={trade} allowedSlippage={allowedSlippage} />
          {showRoute && (
            <>
              <SectionBreak />
              <AutoColumn style={{ padding: '0 24px' }}>
                <RowBetween>
                  <RowFixed>
                    <TYPE.black fontSize={14} fontWeight={400} color={theme.text2}>
                      {t('route')}
                    </TYPE.black>
                    <QuestionHelper text={t('routeDescription')} />
                  </RowFixed>
                  {/* 更多路由选项 - 只在有多个路由时显示 */}
                  {trades && trades.length > 1 && (
                    <ClickableText
                      fontWeight={500}
                      fontSize={12}
                      color={theme.primary1}
                      onClick={() => setIsRouteModalOpen(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {t('More Routes')} ({trades.length})
                      <ChevronDown size={14} />
                    </ClickableText>
                  )}
                </RowBetween>
                <SwapRoute trade={trade} />
              </AutoColumn>
            </>
          )}
        </>
      )}

      {/* 路由选择弹窗 */}
      {trades && trades.length > 1 && onTradeSelect && (
        <RouteSelectionModal
          isOpen={isRouteModalOpen}
          onDismiss={() => setIsRouteModalOpen(false)}
          trades={trades}
          selectedTrade={selectedTrade || null}
          onTradeSelect={onTradeSelect}
        />
      )}
    </AutoColumn>
  )
}
